import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { theme } from "./theme";
import productImg from "../assets/detoxShot.jpg";

gsap.registerPlugin(ScrollTrigger);

const INGREDIENTS = [
  {
    id: 1,
    name: "Lëng Limoni",
    emoji: "🍋",
    desc: "I shtrydhur ftohtë. Mbështet detoksifikimin e mëlçisë dhe forcon sistemin imunitar.",
    side: "left",
  },
  {
    id: 2,
    name: "Lëng Portokalli",
    emoji: "🍊",
    desc: "I pasur me vitaminë C natyrale. Jep energji dhe mbron qelizat nga stresi oksidativ.",
    side: "right",
  },
  {
    id: 3,
    name: "Xhenxhefil",
    emoji: "🫚",
    desc: "I shtypur nga rrënja e plotë. Lufton inflamacionin dhe ndez zjarrin metabolik.",
    side: "left",
  },
  {
    id: 4,
    name: "Turmeric",
    emoji: "🌿",
    desc: "Kurkumina e fuqishme mbështet antiinflamacionin dhe shëndetin e përgjithshëm.",
    side: "right",
  },
  {
    id: 5,
    name: "Piper i Zi",
    emoji: "🫙",
    desc: "Piperini rrit thithjen e kurkuminës deri në 2000%. Shumëfishuesi i heshtur.",
    side: "left",
  },
  {
    id: 6,
    name: "Vitaminë C",
    emoji: "💊",
    desc: "Forcon imunitetin, ndihmon në prodhimin e kolagjenit dhe mbron nga lodhja.",
    side: "right",
  },
];

// Desktop spread positions
const CARD_TARGETS_DESKTOP = [
  { x: -420, y: -160 },
  { x: 420, y: -160 },
  { x: -420, y: 0 },
  { x: 420, y: 0 },
  { x: -420, y: 160 },
  { x: 420, y: 160 },
];

// Tablet spread positions (tighter)
const CARD_TARGETS_TABLET = [
  { x: -260, y: -150 },
  { x: 260, y: -150 },
  { x: -260, y: 0 },
  { x: 260, y: 0 },
  { x: -260, y: 150 },
  { x: 260, y: 150 },
];

function getTargets() {
  if (typeof window === "undefined") return CARD_TARGETS_DESKTOP;
  return window.innerWidth <= 768 ? CARD_TARGETS_TABLET : CARD_TARGETS_DESKTOP;
}

export default function IngredientsSection() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const bottleRef = useRef(null);
  const itemRefs = useRef([]);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 600;

  useLayoutEffect(() => {
    // On very small screens skip the scroll animation — just show cards stacked
    if (isMobile) return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom bottom",
        pin: pinRef.current,
        pinSpacing: false,
      });

      gsap.fromTo(
        bottleRef.current,
        { opacity: 0, scale: 0.75, y: 30 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
            end: "top 50%",
            scrub: 1,
          },
        },
      );

      const targets = getTargets();
      const totalCards = INGREDIENTS.length;

      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const target = targets[i];
        const sliceSize = 80 / totalCards;
        const startPct = 20 + i * sliceSize;
        const endPct = startPct + sliceSize;

        gsap.fromTo(
          el,
          { x: 0, y: 0, opacity: 0, scale: 0.2 },
          {
            x: target.x,
            y: target.y,
            opacity: 1,
            scale: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: `${startPct}% bottom`,
              end: `${endPct}% bottom`,
              scrub: 1.5,
            },
          },
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile]);

  return (
    <SectionWrapper ref={sectionRef} $isMobile={isMobile}>
      {/* ── DESKTOP / TABLET: pinned scroll animation ── */}
      {!isMobile && (
        <PinnedStage ref={pinRef}>
          <StageBg />
          <BgText aria-hidden="true">Detox Shot</BgText>
          <SectionLabel>What's Inside</SectionLabel>

          {INGREDIENTS.map((ing, i) => (
            <IngredientCard
              key={ing.id}
              ref={(el) => (itemRefs.current[i] = el)}
              $side={ing.side}
            >
              <ConnectorLine $side={ing.side} />
              <CardInner>
                <IngEmoji>{ing.emoji}</IngEmoji>
                <IngInfo>
                  <IngName>{ing.name}</IngName>
                  <IngDesc>{ing.desc}</IngDesc>
                </IngInfo>
              </CardInner>
            </IngredientCard>
          ))}

          <BottleZone ref={bottleRef}>
            <BottleGlow />
            <ProductImage src={productImg} alt="OrgaLife Ginger Shot" />
          </BottleZone>

          <CenterLabel>
            <FormulaText>6 active ingredients</FormulaText>
            <FormulaSubtext>zero fillers</FormulaSubtext>
          </CenterLabel>
        </PinnedStage>
      )}

      {/* ── MOBILE: simple stacked layout ── */}
      {isMobile && (
        <MobileLayout>
          <SectionLabel
            style={{
              position: "static",
              transform: "none",
              marginBottom: "1.5rem",
            }}
          >
            What's Inside
          </SectionLabel>

          <MobileBottle>
            <BottleGlow />
            <ProductImage src={productImg} alt="OrgaLife Ginger Shot" />
          </MobileBottle>

          <MobileGrid>
            {INGREDIENTS.map((ing) => (
              <MobileCard key={ing.id}>
                <MobileEmoji>{ing.emoji}</MobileEmoji>
                <MobileInfo>
                  <IngName>{ing.name}</IngName>
                  <IngDesc>{ing.desc}</IngDesc>
                </MobileInfo>
              </MobileCard>
            ))}
          </MobileGrid>

          <CenterLabel
            style={{ position: "static", transform: "none", marginTop: "2rem" }}
          >
            <FormulaText>6 active ingredients</FormulaText>
            <FormulaSubtext>zero fillers</FormulaSubtext>
          </CenterLabel>
        </MobileLayout>
      )}
    </SectionWrapper>
  );
}

/* ─── Styled Components ─────────────────────────────── */

const SectionWrapper = styled.section`
  position: relative;
  height: ${({ $isMobile }) => ($isMobile ? "auto" : "250vh")};
  background: #6f7c40;
`;

const PinnedStage = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StageBg = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 60% 50% at 50% 50%,
    rgba(212, 128, 10, 0.06) 0%,
    transparent 70%
  );
  pointer-events: none;
`;

const BgText = styled.span`
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: 900;
  font-size: clamp(5rem, 16vw, 14rem);
  font-style: italic;
  color: #0000001d;
  -webkit-text-stroke: 1px rgba(212, 128, 10, 0.13);
  z-index: 1;
  width: 100%;
  text-align: center;
  pointer-events: none;
`;

const SectionLabel = styled.p`
  position: absolute;
  top: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${theme.colors.accent};
  z-index: 10;
`;

const IngredientCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 0;
  opacity: 0;
  z-index: 4;
  flex-direction: ${({ $side }) => ($side === "left" ? "row-reverse" : "row")};
  will-change: transform, opacity;
`;

const CardInner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: #96a560;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 1rem 1.2rem;
  max-width: 280px;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.35);

  @media (max-width: 1024px) {
    max-width: 200px;
    padding: 0.75rem 1rem;
  }

  @media (max-width: 768px) {
    max-width: 150px;
    padding: 0.6rem 0.75rem;
  }
`;

const ConnectorLine = styled.div`
  width: clamp(12px, 2vw, 36px);
  height: 1px;
  background: linear-gradient(
    ${({ $side }) => ($side === "left" ? "to left" : "to right")},
    transparent,
    ${theme.colors.accent}
  );
  flex-shrink: 0;
`;

const IngEmoji = styled.span`
  font-size: 1.3rem;
  line-height: 1;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const IngInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const IngName = styled.p`
  font-family: ${theme.fonts.display};
  font-size: 0.82rem;
  font-weight: 700;
  color: ${theme.colors.cream};
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 0.72rem;
  }
`;

const IngDesc = styled.p`
  font-size: 0.65rem;
  color: #e0dddd;
  line-height: 1.5;
  margin-top: 0.1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const BottleZone = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform, opacity;
`;

const ProductImage = styled.img`
  height: clamp(180px, 28vh, 340px);
  filter: drop-shadow(0 24px 48px rgba(0, 0, 0, 0.55));
  position: relative;
  z-index: 1;
`;

const BottleGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(212, 128, 10, 0.22) 0%,
    transparent 70%
  );
  pointer-events: none;
  animation: breathe 3s ease-in-out infinite;

  @keyframes breathe {
    0%,
    100% {
      opacity: 0.6;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.15);
    }
  }
`;

const CenterLabel = styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 10;
`;

const FormulaText = styled.p`
  font-family: ${theme.fonts.display};
  font-size: 1.1rem;
  font-style: italic;
  color: ${theme.colors.creamMuted};
`;

const FormulaSubtext = styled.p`
  font-family: ${theme.fonts.mono};
  font-size: 0.65rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: ${theme.colors.accent};
  margin-top: 0.3rem;
`;

/* ── Mobile-only layout ── */

const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1.25rem 3rem;
  gap: 0;
`;

const MobileBottle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const MobileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  width: 100%;
  max-width: 420px;
`;

const MobileCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  background: #96a560;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 0.85rem 0.9rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const MobileEmoji = styled.span`
  font-size: 1.4rem;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 1px;
`;

const MobileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  p:last-child {
    font-size: 0.62rem;
    color: #e0dddd;
    line-height: 1.5;
    display: block; /* override the desktop hide */
  }
`;
